import { Box, Divider, Group, Modal, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconArrowLeft, IconX } from '@tabler/icons-react';
import { Fragment, ReactNode, useState } from 'react';
import { useIsMobile } from 'src/common/hooks/useIsMobile';
import {
  DESKTOP_PLATFORM_LABELS,
  DESKTOP_PLATFORMS,
  type DesktopPlatform,
  useLatestDesktopRelease,
} from 'src/desktop-downloads/latestDesktopRelease';
import { Button } from 'src/design-system/Button';
import { IconButton } from 'src/design-system/IconButton';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import androidLogo from './assets/android-logo.svg';
import androidQr from './assets/android-qr.svg';
import appleLogo from './assets/apple-logo.svg';
import desktopSnapshot from './assets/desktop-snapshot.png';
import iosQr from './assets/ios-qr.svg';
import mobileSnapshot from './assets/mobile-snapshot.png';
import windowsLogo from './assets/windows-logo.svg';
import classes from './GetApps.module.css';

const MOBILE_PLATFORMS = ['ios', 'android'] as const;

type MobilePlatform = (typeof MOBILE_PLATFORMS)[number];

const MOBILE_PLATFORM_LABELS: Record<MobilePlatform, string> = {
  ios: 'iOS',
  android: 'Android',
};

const MOBILE_STORE_URLS: Record<MobilePlatform, string> = {
  ios: 'https://apps.apple.com/app/id6758049144',
  android: 'https://play.google.com/store/apps/details?id=uk.co.afternoon.app',
};

const PLATFORM_LOGOS: Record<DesktopPlatform | MobilePlatform, string> = {
  'darwin/arm64': appleLogo,
  'win32/x64': windowsLogo,
  ios: appleLogo,
  android: androidLogo,
};

export default function GetAppsModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={768}
      centered
      withCloseButton={false}
      classNames={{ content: classes.modalContent }}
      styles={{ body: { padding: 32 } }}
    >
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
          <Text component="h2" m={0} fz="textXl" lh="30px" fw={FONT_WEIGHTS.semibold} c="gray.10">
            Let Afternoon take the notes, wherever you meet
          </Text>
          <CardIconButton label="Close" onClick={onClose}>
            <IconX size={20} stroke={1.5} />
          </CardIconButton>
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <DesktopAppCard />
          <MobileAppCard />
        </SimpleGrid>
      </Stack>
    </Modal>
  );
}

function DesktopAppCard() {
  const { data: release, isLoading, isError } = useLatestDesktopRelease();

  return (
    <Stack gap="sm" className={classes.card}>
      <CardHeading
        title="Desktop app - for online meetings"
        description="Records your video calls, automates the follow-up."
      />
      <Box className={classes.snapshot}>
        <Box className={classes.snapshotCrop}>
          <img src={desktopSnapshot} alt="" className={classes.desktopSnapshot} />
        </Box>
      </Box>
      {isError ? (
        <Text fz="textSm" lh="20px" fw={FONT_WEIGHTS.medium} c="gray.7">
          Downloads are unavailable right now. Try again later.
        </Text>
      ) : (
        <PlatformList
          platforms={DESKTOP_PLATFORMS}
          renderRow={platform => (
            <PlatformRow logo={PLATFORM_LOGOS[platform]} label={DESKTOP_PLATFORM_LABELS[platform]}>
              <DownloadButton
                href={release?.installers[platform]?.url}
                loading={isLoading}
                label={`Download for ${DESKTOP_PLATFORM_LABELS[platform]}`}
              />
            </PlatformRow>
          )}
        />
      )}
    </Stack>
  );
}

function MobileAppCard() {
  const { isMobile } = useIsMobile();
  const [qrPlatform, setQrPlatform] = useState<MobilePlatform>();

  if (qrPlatform) {
    return <MobileQrCard platform={qrPlatform} onBack={() => setQrPlatform(undefined)} />;
  }

  return (
    <Stack gap="sm" className={classes.card}>
      <CardHeading
        title="Mobile app - for in-person meetings"
        description="Put your phone on the table, record and automate the follow-up."
      />
      <Box className={classes.snapshot}>
        <Box className={classes.snapshotCrop}>
          <img src={mobileSnapshot} alt="" className={classes.mobileSnapshot} />
        </Box>
      </Box>
      <PlatformList
        platforms={MOBILE_PLATFORMS}
        renderRow={platform => (
          <PlatformRow logo={PLATFORM_LOGOS[platform]} label={MOBILE_PLATFORM_LABELS[platform]}>
            <DownloadButton
              href={isMobile ? MOBILE_STORE_URLS[platform] : undefined}
              onClick={isMobile ? undefined : () => setQrPlatform(platform)}
              label={`Download for ${MOBILE_PLATFORM_LABELS[platform]}`}
            />
          </PlatformRow>
        )}
      />
    </Stack>
  );
}

function MobileQrCard({ platform, onBack }: { platform: MobilePlatform; onBack: () => void }) {
  const isIos = platform === 'ios';

  return (
    <Stack gap="sm" className={classes.card}>
      <Group gap="xxs" wrap="nowrap">
        <CardIconButton label="Back" onClick={onBack}>
          <IconArrowLeft size={20} stroke={2} />
        </CardIconButton>
        <Text fz="textMd" lh="24px" fw={FONT_WEIGHTS.semibold} c="gray.10">
          {isIos ? 'Scan to get the iOS app' : 'Scan to get the Android app'}
        </Text>
      </Group>
      <Stack flex={1} gap="xs" align="center" justify="center">
        <img
          src={isIos ? iosQr : androidQr}
          alt={isIos ? 'QR code for the iOS app' : 'QR code for the Android app'}
          width={180}
          height={180}
        />
        <Text fz="textXs" lh="18px" fw={FONT_WEIGHTS.medium} c="gray.7" ta="center">
          {isIos ? 'Point your iPhone camera at the code' : 'Point your Android camera at the code'}
        </Text>
      </Stack>
    </Stack>
  );
}

function CardHeading({ title, description }: { title: string; description: string }) {
  return (
    <Stack gap="xxxs">
      <Text fz="textMd" lh="24px" fw={FONT_WEIGHTS.semibold} c="gray.10">
        {title}
      </Text>
      <Text fz="textSm" lh="20px" fw={FONT_WEIGHTS.medium} c="gray.7">
        {description}
      </Text>
    </Stack>
  );
}

function PlatformList<Platform extends string>({
  platforms,
  renderRow,
}: {
  platforms: readonly Platform[];
  renderRow: (platform: Platform) => ReactNode;
}) {
  return (
    <Stack gap={0}>
      {platforms.map((platform, index) => (
        <Fragment key={platform}>
          {index > 0 && <Divider color="gray.1" />}
          {renderRow(platform)}
        </Fragment>
      ))}
    </Stack>
  );
}

function PlatformRow({
  logo,
  label,
  children,
}: {
  logo: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <Group gap="xxs" wrap="nowrap" className={classes.platformRow}>
      <img src={logo} alt="" width={18} height={18} />
      <Text flex={1} fz="textSm" lh="20px" fw={FONT_WEIGHTS.medium} c="gray.10">
        {label}
      </Text>
      {children}
    </Group>
  );
}

function DownloadButton({
  href,
  onClick,
  loading,
  label,
}: {
  href?: string;
  onClick?: () => void;
  loading?: boolean;
  label: string;
}) {
  const props = href ? { component: 'a' as const, href } : { onClick, disabled: !onClick };

  return (
    <Button
      variant="secondary"
      size="xs"
      fz="textSm"
      loading={loading}
      aria-label={label}
      style={{ borderRadius: 10 }}
      {...props}
    >
      Download
    </Button>
  );
}

function CardIconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <IconButton
      size={28}
      bg="brand.2"
      c="brand.6"
      aria-label={label}
      onClick={onClick}
      style={{ borderRadius: 10 }}
    >
      {children}
    </IconButton>
  );
}
